import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ChatsChannelBase, ChatsChannelSetting } from '../../constants';
import { isStandalone } from '../../constants';
import { defaultSettingsPage } from './options';
import { channelSettingsGetters, useChannelSettings } from './utils';

export const ChannelsContext = createContext<ChatsChannelSetting[]>([]);

const getChannels = (channels: ChatsChannelBase[]) => {
  if (isStandalone) {
    return channels;
  }

  return channels?.length
    ? channels
    : (defaultSettingsPage as ChatsChannelBase[]);
};

const updatedSettings = (
  settings: Record<string, string | object | null | undefined>,
  defaultSettings: Partial<ChatsChannelBase>
) => {
  for (const key in settings) {
    const typedKey = key as keyof ChatsChannelBase;
    if (typeof settings[typedKey] === 'object' && settings[typedKey] !== null) {
      const currentDefaultSettings = defaultSettings[typedKey];

      if (!currentDefaultSettings) {
        return settings;
      }

      updatedSettings(
        settings[typedKey] as Record<string, object>,
        currentDefaultSettings as Record<string, string | object | undefined>
      );
    }
    const shouldApplyDefault =
      !settings[typedKey] &&
      Object.prototype.hasOwnProperty.call(defaultSettings, typedKey);
    if (shouldApplyDefault) {
      settings[typedKey] = defaultSettings[typedKey] as
        | string
        | object
        | undefined;
    }
  }

  return settings;
};

export const ChannelsProvider = ({ children }: PropsWithChildren) => {
  const { channels, defaultChannels } = useChannelSettings();

  const value = useMemo(() => {
    const currentChannel = getChannels(channels);
    return currentChannel.reduce((acc: ChatsChannelSetting[], channel) => {
      const settingsGetter = channelSettingsGetters[channel.type];
      if (settingsGetter) {
        const settings = updatedSettings(channel, defaultChannels);
        const settingsResult = settingsGetter({
          ...defaultChannels,
          ...settings
        });
        if (settingsResult) {
          acc.push(settingsResult);
        }
      }

      return acc;
    }, []);
  }, [channels, defaultChannels]);

  return (
    <ChannelsContext.Provider value={value}>
      {children}
    </ChannelsContext.Provider>
  );
};

export const useChannels = () => {
  return useContext(ChannelsContext);
};
